import { useEffect, useRef, useState, useId } from 'react';

interface MoneyInputProps {
  /** Wert in Cents (interner Source-of-Truth). `null` = leer. */
  valueCents: number | null;
  /**
   * Callback bei Commit. `null` = User hat alles gelöscht.
   *
   * WICHTIG: Wird NICHT bei jedem Tastendruck gefeuert. Stattdessen:
   *   - bei Blur (Feld verlässt Fokus)
   *   - bei Enter
   *   - nach 300 ms ohne weiteren Tastendruck (Debounce)
   *
   * Bis zum Commit puffert die Komponente den Eingabewert intern. Dadurch
   * verschwindet der Glitch, dass „4." kurz als 4 € durch den Live-Score
   * läuft, und Re-Renders der Score-Engine werden gebündelt.
   */
  onChange: (cents: number | null) => void;
  label: string;
  hint?: string;
  placeholder?: string;
  /** Optional: zusätzlicher Fehler-State (z.B. Pflicht-Validation). */
  errorMessage?: string;
  /** Optional: nur anzeigen, nicht editierbar. Read-only zeigt die große Zahl ohne Border. */
  readOnly?: boolean;
  /** Erlaubt negative Beträge (z.B. negative Liquidität). Default false. */
  allowNegative?: boolean;
  /** Optionaler ID-Override für Label-Verknüpfung in Tests. */
  inputId?: string;
  /** Debounce-Delay in ms. Default 300. Tests können kürzere Werte injizieren. */
  debounceMs?: number;
}

/**
 * Editorial-Money-Input mit deutschem Locale.
 *
 * Anzeige: große, tabular-numbers, Source-Serif-Black, mit nachgestelltem
 * „€"-Suffix. Eingabe als String, Konvertierung zu Cents per
 * `parseGermanEuroToCents`. Beim Blur wird normalisiert (z.B. „4500" →
 * „4.500,00 €").
 *
 * Commit-Modell (siehe `onChange`): Daten werden bei Blur, Enter oder nach
 * 300 ms Idle committet. Das verhindert Re-Render-Stürme im Live-Score und
 * den UX-Glitch, dass „4." als 4 € interpretiert wird, während der User
 * noch tippt.
 *
 * Read-only-Variante zeigt die große Zahl ohne Border – passend für
 * abgeleitete Werte (Gewinn, Stundensatz, Reichweite).
 */
export function MoneyInput({
  valueCents,
  onChange,
  label,
  hint,
  placeholder,
  errorMessage,
  readOnly = false,
  allowNegative = false,
  inputId,
  debounceMs = 300,
}: MoneyInputProps) {
  const generatedId = useId();
  const id = inputId ?? generatedId;
  const [draft, setDraft] = useState<string>(() => formatCentsForEdit(valueCents));
  const [hasFocus, setHasFocus] = useState(false);

  // Debounce-Timer + Ref auf das letzte committed Cents, damit wir
  // duplikate Commits vermeiden (z.B. Debounce-Trigger und gleich danach
  // Blur — beide würden onChange feuern, ein Aufruf reicht).
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedRef = useRef<number | null>(valueCents);

  // Bei externen Änderungen (Reset, Default-Wert) den Draft aktualisieren —
  // aber nur, wenn das Feld nicht gerade fokussiert ist (sonst überschreiben
  // wir die Tipparbeit des Users).
  useEffect(() => {
    if (!hasFocus) {
      setDraft(formatCentsForEdit(valueCents));
      lastCommittedRef.current = valueCents;
    }
  }, [valueCents, hasFocus]);

  // Debounce-Timer beim Unmount aufräumen.
  useEffect(() => {
    return () => {
      if (commitTimer.current !== null) clearTimeout(commitTimer.current);
    };
  }, []);

  const commit = (raw: string) => {
    if (commitTimer.current !== null) {
      clearTimeout(commitTimer.current);
      commitTimer.current = null;
    }
    const cents = parseGermanEuroToCents(raw, allowNegative);
    if (cents !== lastCommittedRef.current) {
      lastCommittedRef.current = cents;
      onChange(cents);
    }
  };

  const scheduleDebouncedCommit = (raw: string) => {
    if (commitTimer.current !== null) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => {
      commitTimer.current = null;
      commit(raw);
    }, debounceMs);
  };

  const display = hasFocus ? draft : formatCentsForDisplay(valueCents);

  if (readOnly) {
    return (
      <div className="block">
        <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {label}
        </span>
        {hint && (
          <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">{hint}</p>
        )}
        <div className="mt-3 flex items-baseline gap-2">
          <span
            id={id}
            className="font-serif text-3xl font-black tabular-nums tracking-tight text-stone-900 md:text-4xl"
          >
            {valueCents === null ? '—' : formatCentsForDisplay(valueCents)}
          </span>
          {valueCents !== null && (
            <span aria-hidden="true" className="font-serif text-xl text-stone-500">
              €
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="block">
      <label htmlFor={id}>
        <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {label}
        </span>
        {hint && (
          <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">{hint}</p>
        )}
      </label>
      <div
        className={`mt-3 flex items-baseline gap-2 border-b transition-colors ${
          errorMessage ? 'border-rose-500' : hasFocus ? 'border-stone-900' : 'border-stone-300'
        }`}
      >
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={display}
          placeholder={placeholder ?? '0,00'}
          onFocus={() => {
            setHasFocus(true);
            setDraft(formatCentsForEdit(valueCents));
          }}
          onBlur={() => {
            setHasFocus(false);
            commit(draft);
            // Draft auf normalisierte Edit-Form setzen, damit beim nächsten
            // Fokus die zuletzt committed Zahl im Edit-Format erscheint.
            setDraft(formatCentsForEdit(parseGermanEuroToCents(draft, allowNegative)));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit(draft);
            }
          }}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            // Kein synchrones onChange — wir committen erst nach Debounce/Blur/Enter.
            scheduleDebouncedCommit(next);
          }}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={errorMessage ? `${id}-err` : undefined}
          className="w-full bg-transparent py-2 font-serif text-3xl font-black tabular-nums tracking-tight text-stone-900 outline-none placeholder:text-stone-300 md:text-4xl"
        />
        <span aria-hidden="true" className="font-serif text-xl text-stone-500">
          €
        </span>
      </div>
      {errorMessage && (
        <p id={`${id}-err`} className="mt-2 font-sans text-xs font-medium text-rose-600">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

/**
 * Parst einen User-Input-String („4.500,00", „4500", „4500.50") nach Cents.
 * Akzeptiert sowohl Komma als auch Punkt als Dezimaltrenner — typische
 * Realität bei Copy-Paste aus Excel oder PDF.
 *
 * Liefert:
 *   - null bei leerem oder unparseable Input
 *   - integer cents sonst
 */
export function parseGermanEuroToCents(input: string, allowNegative = false): number | null {
  const trimmed = input.trim();
  if (trimmed === '' || trimmed === '-') return null;

  // Erlaubte Zeichen: Ziffern, Komma, Punkt, optionales führendes Minus, € und Whitespace
  // Wir entfernen €, alle Whitespaces und Tausenderpunkte.
  const cleaned = trimmed.replace(/[€\s]/g, '');
  if (!/^-?[\d.,]+$/.test(cleaned)) return null;

  const isNegative = cleaned.startsWith('-');
  const abs = cleaned.replace(/^-/, '');

  // Heuristik:
  //  - „4.500,00" (DE Standard): Punkt = Tausender, Komma = Dezimal
  //  - „4500.50"  (US Pure):     Punkt = Dezimal
  //  - „4500,5"   (DE einfach):  Komma = Dezimal
  //  - „4500"     (Plain):       integer
  let euros: number | null = null;
  if (abs.includes(',')) {
    // Komma als Dezimal — Punkte als Tausender entfernen
    const parts = abs.split(',');
    if (parts.length !== 2) return null;
    const intPart = parts[0]!.replace(/\./g, '');
    const decPart = parts[1]!.padEnd(2, '0').slice(0, 2);
    if (!/^\d+$/.test(intPart) || !/^\d+$/.test(decPart)) return null;
    euros = Number(`${intPart}.${decPart}`);
  } else if (abs.includes('.')) {
    // Punkt — könnte Dezimal oder Tausender sein. Heuristik: wenn nur EIN
    // Punkt UND nach dem Punkt 1-2 Ziffern → Dezimal (US-Stil). Sonst Tausender.
    const parts = abs.split('.');
    const last = parts[parts.length - 1]!;
    if (parts.length === 2 && last.length <= 2) {
      // US-Dezimal
      euros = Number(`${parts[0]}.${last.padEnd(2, '0')}`);
    } else {
      // Tausender
      const joined = parts.join('');
      if (!/^\d+$/.test(joined)) return null;
      euros = Number(joined);
    }
  } else {
    if (!/^\d+$/.test(abs)) return null;
    euros = Number(abs);
  }

  if (euros === null || Number.isNaN(euros)) return null;
  const signed = isNegative ? -euros : euros;
  if (!allowNegative && signed < 0) return null;
  return Math.round(signed * 100);
}

/** Cents → „4.500,00" (für Anzeige im Idle-Zustand). */
export function formatCentsForDisplay(cents: number | null): string {
  if (cents === null) return '';
  const euros = cents / 100;
  return euros.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Cents → „4500,00" (kein Tausenderpunkt — leichter zu editieren beim Tippen). */
export function formatCentsForEdit(cents: number | null): string {
  if (cents === null) return '';
  const euros = cents / 100;
  // Im Edit-Mode lassen wir den User bewusst ohne Tausenderpunkte tippen,
  // damit Cursor-Position beim Editieren nicht springt.
  return euros.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}
